"use client";

import React, { useState } from "react";
import Image from "next/image";
import { renderTextWithBreaks } from "@/utils/stripHTML";
import { cn } from "@/utils/cn";

export interface SettlementStep {
  id: string;
  stepNumber: string;
  titlenumber: string;
  subtitle: string;
  title: string;
  description: string;
  isHighlighted?: boolean;
}

export interface SettlementSolutionData {
  sectionIndex?: string;
  totalSections?: string;
  tag?: string;
  title?: string;
  description?: string;
  ctaButton?: {
    label: string;
    url?: string;
  };
  bottomNote?: string;
  disclaimer?: string;
  steps?: SettlementStep[];
}

interface SettlementSolutionSectionProps {
  data: SettlementSolutionData;
  onCtaClick?: () => void;
  onStepClick?: (stepId: string) => void;
}

export default function SettlementSolutionSection({
  data,
  onCtaClick,
  onStepClick,
}: SettlementSolutionSectionProps) {
  if (!data || !data.steps || data.steps.length === 0) return null;

  const {
    tag = "",
    title = "",
    description = "",
    bottomNote = "",
    disclaimer = "",
    steps = [],
  } = data;

  const [activeStepId, setActiveStepId] = useState<string>(
    steps.find((s) => s.isHighlighted)?.id || steps[0]?.id || "step-1",
  );

  const handleStepSelect = (stepId: string) => {
    setActiveStepId(stepId);
    if (onStepClick) {
      onStepClick(stepId);
    }
  };

  const stepCount = steps.length;
  const isMultiRow = stepCount >= 5;

  const renderCard = (step: (typeof steps)[0], extraClass = "") => {
    const isSelected = activeStepId === step.id;

    return (
      <div
        key={step.id}
        onClick={() => handleStepSelect(step.id)}
        className={cn(
          "p-5 sm:p-6 md:p-7 flex flex-col justify-between transition-all duration-500 cursor-pointer select-none group border relative overflow-hidden",
          isMultiRow ? "min-h-[200px] sm:min-h-[220px]" : "min-h-[220px]",
          isSelected
            ? "bg-[#E5D4B2] text-[#193B2F] border-[#E5D4B2] shadow-2xl"
            : "bg-[#12362A] text-white border-white/10 hover:border-white/30 hover:bg-[#153D30]",
          extraClass,
        )}
      >
        <div
          className={cn(
            "flex items-center justify-between gap-2 border-b pb-3.5 transition-colors",
            isSelected ? "border-[#193B2F]/15" : "border-white/10",
          )}
        >
          <div className="flex items-center gap-2">
            <span
              className={cn(
                "text-[9px] sm:text-[10px] font-sans uppercase tracking-widest",
                isSelected ? "text-[#193B2F]/60" : "text-white/50",
              )}
            >
              BƯỚC {step.stepNumber}
            </span>
            <span
              className={cn(
                "text-[9px] sm:text-[10px] font-sans font-bold uppercase tracking-wider",
                isSelected ? "text-[#193B2F]" : "text-white/80",
              )}
            >
              {step.subtitle}
            </span>
          </div>

          <Image
            src="/icons/arrow_outward.svg"
            alt="arrow"
            width={12}
            height={12}
            className={cn(
              "w-3.5 h-3.5 object-contain transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5",
              isSelected ? "" : "invert brightness-200",
            )}
          />
        </div>

        {/* MIDDLE CONTENT: STEP NUMBER & TITLE */}
        <div className="my-auto space-y-2 py-3 sm:py-4">
          <div
            className={cn(
              "font-lora text-4xl sm:text-5xl lg:text-6xl font-normal leading-none",
              isSelected ? "text-[#193B2F]" : "text-white/80",
            )}
          >
            {step.titlenumber}
          </div>

          <h3
            className={cn(
              "font-lora text-lg sm:text-xl md:text-2xl font-normal leading-snug pt-2",
              isSelected ? "text-[#193B2F]" : "text-white",
            )}
          >
            {renderTextWithBreaks(step.title)}
          </h3>
        </div>

        {/* BOTTOM DESCRIPTION */}
        <p
          className={cn(
            "text-xs font-sans font-light leading-relaxed pt-2 border-t",
            isSelected
              ? "text-[#193B2F]/80 border-[#193B2F]/15"
              : "text-white/70 border-white/10",
          )}
        >
          {renderTextWithBreaks(step.description)}
        </p>
      </div>
    );
  };

  const renderStepsContent = () => {
    if (stepCount === 1) {
      return (
        <div className="w-full flex justify-center my-2">
          {renderCard(steps[0], "w-full lg:w-1/2 max-w-2xl")}
        </div>
      );
    }

    if (stepCount === 2) {
      return (
        <div className="w-full flex-1 min-h-0 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-4 lg:gap-5 my-2 items-stretch">
          {steps.map((step) => renderCard(step))}
        </div>
      );
    }

    if (stepCount === 3) {
      return (
        <div className="w-full flex-1 min-h-0 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5 my-2 items-stretch">
          {steps.map((step) => renderCard(step))}
        </div>
      );
    }

    if (stepCount === 4) {
      return (
        <div className="w-full flex-1 min-h-0 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5 my-2 items-stretch">
          {steps.map((step) => renderCard(step))}
        </div>
      );
    }

    if (stepCount === 5) {
      const row1 = steps.slice(0, 3);
      const row2 = steps.slice(3, 5);
      return (
        <div className="w-full flex-1 min-h-0 flex flex-col gap-4 lg:gap-5 my-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5 w-full items-stretch">
            {row1.map((step) => renderCard(step))}
          </div>
          <div className="flex flex-col sm:flex-row justify-center gap-4 lg:gap-5 w-full items-stretch">
            {row2.map((step) =>
              renderCard(
                step,
                "w-full sm:w-[calc(50%-0.5rem)] lg:w-[calc(33.333%-0.85rem)]",
              ),
            )}
          </div>
        </div>
      );
    }

    if (stepCount === 6) {
      return (
        <div className="w-full flex-1 min-h-0 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5 my-2 items-stretch">
          {steps.map((step) => renderCard(step))}
        </div>
      );
    }

    if (stepCount === 7) {
      const row1 = steps.slice(0, 4);
      const row2 = steps.slice(4, 7);
      return (
        <div className="w-full flex-1 min-h-0 flex flex-col gap-4 lg:gap-5 my-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5 w-full items-stretch">
            {row1.map((step) => renderCard(step))}
          </div>
          <div className="flex flex-col sm:flex-row justify-center gap-4 lg:gap-5 w-full items-stretch">
            {row2.map((step) =>
              renderCard(
                step,
                "w-full sm:w-[calc(50%-0.5rem)] lg:w-[calc(25%-0.95rem)]",
              ),
            )}
          </div>
        </div>
      );
    }

    // Fallback for 8+ steps
    return (
      <div className="w-full flex-1 min-h-0 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5 my-2 items-stretch">
        {steps.map((step) => renderCard(step))}
      </div>
    );
  };

  return (
    <section
      id="giai-phap-an-cu"
      className={cn(
        "relative w-full flex flex-col justify-between overflow-hidden bg-[#0D291E] text-white select-none px-6 sm:px-10 lg:px-14 xl:px-16 pt-16 sm:pt-20 lg:pt-24 pb-8 sm:pb-10 lg:pb-8",
        isMultiRow
          ? "min-h-screen h-auto py-16"
          : "min-h-screen lg:h-screen lg:max-h-screen",
      )}
    >
      <style>{`
        @keyframes rippleExpand {
          0% {
            transform: translate(50%, -50%) scale(0.85);
            opacity: 0.2;
          }
          50% {
            transform: translate(50%, -50%) scale(1.08);
            opacity: 0.65;
          }
          100% {
            transform: translate(50%, -50%) scale(0.85);
            opacity: 0.2;
          }
        }
      `}</style>

      {/* BACKGROUND GRADIENT IN TAILWIND PRIMARY & PRIMARY DARK PALETTE */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary via-[#085C47] to-primaryDark pointer-events-none z-0 opacity-95" />
      <div className="absolute top-0 left-0 w-96 h-96 bg-primary/25 rounded-full blur-3xl pointer-events-none z-0" />

      {/* CONCENTRIC EXPANDING RIPPLE CIRCLES CENTERED AT EXACT TOP-RIGHT CORNER (top-0 right-0) */}
      <div className="absolute top-0 right-0 w-0 h-0 pointer-events-none z-[1]">
        <div
          className="absolute top-0 right-0 w-[1400px] h-[1400px] rounded-full border border-white/20"
          style={{ animation: "rippleExpand 7s ease-in-out infinite" }}
        />
        <div
          className="absolute top-0 right-0 w-[1100px] h-[1100px] rounded-full border border-white/25"
          style={{ animation: "rippleExpand 6.5s ease-in-out infinite 0.8s" }}
        />
        <div
          className="absolute top-0 right-0 w-[820px] h-[820px] rounded-full border border-[#C9AB81]/35"
          style={{ animation: "rippleExpand 6s ease-in-out infinite 1.6s" }}
        />
        <div
          className="absolute top-0 right-0 w-[560px] h-[560px] rounded-full border border-white/30"
          style={{ animation: "rippleExpand 5.5s ease-in-out infinite 0.4s" }}
        />
        <div
          className="absolute top-0 right-0 w-[320px] h-[320px] rounded-full border border-[#C9AB81]/45 bg-[#C9AB81]/10"
          style={{ animation: "rippleExpand 5s ease-in-out infinite 1.2s" }}
        />
      </div>

      {/* LEFT DECORATIVE SHADOW OVERLAY IMAGE */}
      <div className="absolute inset-y-0 left-0 w-full sm:w-[65%] lg:w-[55%] h-full pointer-events-none z-[1] opacity-100 mix-blend-overlay">
        <Image
          src="/images/deco_shadow_left_bottom.png"
          alt="Decorative Shadow Overlay"
          fill
          sizes="(max-width: 1024px) 100vw, 55vw"
          priority
          className="object-cover object-left-bottom"
        />
      </div>

      <div className="relative z-10 w-full h-full flex flex-col justify-between overflow-hidden gap-2">
        {/* ================= 1. HEADER ROW ================= */}
        <div className="w-full flex flex-col lg:flex-row justify-between items-end gap-3 lg:gap-6 flex-shrink-0">
          {/* LEFT: TAG & MAIN TITLE */}
          <div className="w-full lg:w-[58%] space-y-1.5">
            <div className="flex items-center gap-3">
              <span className="w-7 h-[1px] bg-[#C9AB81]" />
              <span className="text-[11px] sm:text-xs font-semibold uppercase tracking-[0.22em] text-[#C9AB81] font-sans">
                {tag}
              </span>
            </div>

            <h2
              className="text-3xl sm:text-4xl md:text-5xl lg:text-5xl font-lora font-normal text-white tracking-tight title-leading"
              style={{ lineHeight: 1.15 }}
            >
              {renderTextWithBreaks(title)}
            </h2>
          </div>

          {/* RIGHT: COUNTER, DESCRIPTION & PILL CTA */}
          <div className="w-full lg:w-[38%] flex flex-col items-start lg:items-end text-left lg:text-right space-y-2 lg:pb-0.5">
            <p className="text-xs font-light font-sans text-white/70 leading-relaxed max-w-md text-justify lg:text-right">
              {renderTextWithBreaks(description)}
            </p>
          </div>
        </div>

        {/* ================= 2. STEPS CARDS ROW ================= */}
        {renderStepsContent()}

        {/* ================= 3. BOTTOM FOOTER NOTE ROW ================= */}
        <div className="w-full flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 pt-2 border-t border-white/10 flex-shrink-0">
          {/* LEFT: EMBLEM & STATEMENT */}
          <div className="flex items-center gap-3 max-w-2xl">
            <div className="w-5 h-5 rounded-none border border-[#C9AB81] rotate-45 flex items-center justify-center shrink-0">
              <div className="w-2 h-2 bg-[#C9AB81] rounded-none" />
            </div>
            <p className="font-lora italic text-xs sm:text-sm text-white/90 leading-snug">
              {bottomNote}
            </p>
          </div>

          {/* RIGHT: DISCLAIMER NOTE */}
          <div className="text-[8px] sm:text-[9px] font-sans font-medium text-white/40 tracking-wider uppercase text-left sm:text-right shrink-0">
            {disclaimer}
          </div>
        </div>
      </div>
    </section>
  );
}
